<template>
  <DeepDialog
    v-model="showDialog"
    :title="i18n.t('texts.want_to_continue')"
    width="600"
    @keydown.esc="closeDialog"
  >
    <template #content>
      {{ i18n.t('actions.delete_n_documents', _documents.length) }}
    </template>
    <template #actions>
      <VBtnSecondary
        data-test-id="close-dialog-cancel"
        :text="i18n.t('buttons.cancel')"
        variant="outlined"
        @click="closeDialog"
      />

      <VBtnPrimary
        color="error"
        data-test-id="delete-document"
        :loading="deleteDocumentPending"
        :text="i18n.t('buttons.delete')"
        @click="onDeleteDocuments"
      />
    </template>
  </DeepDialog>
</template>

<script setup lang="ts">
import { useMessageStore } from '@/stores/message.ts';
import { onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useEmitter } from '@/composables/useEmitter.ts';
import { useRoute, useRouter } from 'vue-router';
import { DeepDialog } from '@deepcloud/deep-ui-lib';
import type { Document } from '@/types/Document';
import { useApi } from '@/composables/useApi.ts';
import { useDocumentWizard } from '@/composables/create/useDocumentWizard';

const emit = defineEmits(['done']);
const { deepSignAPI } = useApi();
const messageStore = useMessageStore();
const i18n = useI18n();
const emitter = useEmitter();
const route = useRoute();
const router = useRouter();
const { removeDocuments } = useDocumentWizard();

const showDialog = ref(false);
const deleteDocumentPending = ref(false);

emitter.$on('open-delete-documents-dialog', openDialog);

onUnmounted(() => {
  emitter.$off('open-delete-documents-dialog', openDialog);
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _documents = ref<any[]>([]);

function openDialog({ documents }) {
  _documents.value = [...documents];
  showDialog.value = true;
}

function closeDialog() {
  showDialog.value = false;
  _documents.value = [];
}

async function onDeleteDocuments() {
  let isError = false;
  deleteDocumentPending.value = true;
  // Collect the ids that actually deleted so we prune only those setup-wizard
  // preference entries (a failed delete leaves the draft, so it keeps its entry).
  const deletedIds: string[] = [];
  const deletePromises = _documents.value.map((document: Document) =>
    deepSignAPI.value.documents
      .deleteById(document.documentId)
      .then(() => {
        deletedIds.push(document.documentId);
      })
      .catch(() => {
        messageStore.showMessage({
          key: 'errors.api.delete_document',
          color: 'error',
          icon: 'fa fa-circle-exclamation'
        });
      })
  );

  await Promise.all(deletePromises)
    .then(() => {
      if (isError) {
        messageStore.showMessage({
          key: 'errors.api.delete_document',
          color: 'error',
          icon: 'fa fa-circle-exclamation'
        });
      } else {
        messageStore.showMessage({
          text: i18n.t('snackbars.document_deleted', {
            number: deletePromises.length
          }),
          color: 'success',
          icon: 'fa fa-circle-check'
        });
      }
    })
    .catch(() => {
      isError = true;
    })
    .finally(() => {
      if (isError) {
        messageStore.showMessage({
          key: 'errors.api.delete_document',
          color: 'error',
          icon: 'fa fa-circle-exclamation'
        });
      }
      // Drop the setup-wizard entries of the deleted drafts.
      removeDocuments(deletedIds);
      closeDialog();
      emit('done');
      if (route.name !== 'dashboard') {
        router.push({ name: 'dashboard' });
      }
      deleteDocumentPending.value = false;
    });
}
</script>

<style scoped lang="scss">
.card-title {
  word-break: break-word;
  font-weight: 600;
  font-size: 24px !important;
  text-align: center;
  margin: 0 0 24px;
  white-space: normal;
}
</style>
